import { Product } from "@transfr-inc/dashboard-components/constants";

import HttpClient from "@lib/http-client";

/**
 * Rows requested per breakdown page. Sized to cover the collapsed preview (the
 * top ~10 rows) and fill one expanded scroll viewport in a single request, so
 * the common collapsed case fetches a small payload and the expanded list pages
 * the rest in as the viewer scrolls. Well under the spec's 200-row `page_size`
 * maximum.
 */
export const BREAKDOWN_PAGE_SIZE = 25;

/**
 * Rows requested per ranked-career page — the spec's `page_size` maximum, since
 * that list is rendered whole with no paging control.
 */
export const TOP_CAREERS_PAGE_SIZE = 200;

/**
 * Page ceiling for {@link OutcomesService#getTopCareers}: bounds a
 * `next_page_token` cursor that never terminates. Set far above any realistic
 * career catalog so it never truncates genuine data.
 */
export const TOP_CAREERS_MAX_PAGES = 25;

/**
 * Fetches a single breakdown page and normalizes the wire envelope. The rows
 * are already sorted by the server, so a page is a contiguous slice of the
 * ordered list — the caller (an infinite query) follows `next_page_token`
 * to append the next slice as the viewer scrolls, rather than aggregating the
 * whole list up front.
 *
 * `data` and `total_rows` are defended against a malformed body: a non-array
 * `data` becomes `[]`, and a non-integer `total_rows` falls back to the rows on
 * hand. `next_page_token` is normalized to `null` when absent so a caller can
 * treat "no more pages" as a single sentinel.
 *
 * @param {import("axios").AxiosInstance} http
 * @param {string} path
 * @param {object} params - Query params for the page.
 * @param {string} [pageToken] - Cursor for the page to fetch; omitted for the
 *   first page.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
 */
async function fetchBreakdownPage(http, path, params, pageToken, signal) {
  const body = await http.get(path, {
    params: {
      ...params,
      page_size: BREAKDOWN_PAGE_SIZE,
      ...(pageToken ? { page_token: pageToken } : {}),
    },
    signal,
  });

  const data = Array.isArray(body?.data) ? body.data : [];
  return {
    data,
    total_rows: Number.isInteger(body?.total_rows)
      ? body.total_rows
      : data.length,
    next_page_token: body?.next_page_token || null,
  };
}

/**
 * Wire `product_id` for a dashboard `Product`. The outcomes service's product
 * axis is coarser: it accepts only 1 (Train) and 2 (Trek) and rejects the whole
 * request for anything else, so both Trek lines fold onto 2 and a line it can't
 * name (Programs, Basics) has no id here.
 */
const OUTCOMES_PRODUCT_ID = new Map([
  [Product.TS, 1],
  [Product.CE, 2],
  [Product.TRK, 2],
]);

/**
 * The collection filters, mapped from filter-envelope key to wire param name.
 * `sanitize` drops values the backend won't accept; a collection left empty by
 * it sends no param at all.
 */
const COLLECTION_PARAMS = [
  { key: "orgCodes", param: "org_code" },
  { key: "classroomIds", param: "classroom_id" },
  { key: "userIds", param: "user_id" },
  {
    key: "productIds",
    param: "product_id",
    sanitize: (ids) => [
      ...new Set(ids.map((id) => OUTCOMES_PRODUCT_ID.get(id)).filter(Boolean)),
    ],
  },
  { key: "moduleIds", param: "module_id" },
  { key: "programIds", param: "program_id" },
];

/**
 * Serializes the filters into the outcomes proxy's query envelope. An empty
 * array means "no filter" and is omitted rather than sent as a blank value (the
 * backend would otherwise read e.g. `org_code=` as an empty-set filter).
 *
 * Param names and types mirror the proxied Data-Team service: the date range is
 * `start_date`/`end_date` as `YYYY-MM-DD`, orgs are filtered by `org_code`
 * (string codes), and products by `product_id` (see
 * {@link OUTCOMES_PRODUCT_ID}).
 *
 * @param {object} [filters] - Filter envelope
 * @param {Date|string} [filters.startDate] - Inclusive range start
 * @param {Date|string} [filters.endDate] - Inclusive range end
 * @param {string[]} [filters.orgCodes] - Org code filter
 * @param {string[]} [filters.classroomIds] - Classroom id filter (UUID strings)
 * @param {string[]} [filters.userIds] - Learner (user) id filter (UUID strings)
 * @param {number[]} [filters.productIds] - Product id filter (`Product` ints)
 * @param {string[]} [filters.moduleIds] - Module id filter (string ids); valid
 *   only for a module-aware metric (e.g. `sims_mastered`)
 * @param {string[]} [filters.programIds] - Program id filter (`program_id`); a
 *   program is a classroom type, so this scopes to learners enrolled in the
 *   selected programs.
 * @returns {object} Wire query params
 */
export function encodeFilters(filters = {}) {
  const params = {};

  const start = toIsoDate(filters.startDate);
  if (start !== undefined) {
    params.start_date = start;
  }
  const end = toIsoDate(filters.endDate);
  if (end !== undefined) {
    params.end_date = end;
  }

  for (const { key, param, sanitize } of COLLECTION_PARAMS) {
    const requested = filters[key];
    if (!Array.isArray(requested)) {
      continue;
    }
    const values = sanitize ? sanitize(requested) : requested;
    if (values.length > 0) {
      params[param] = values;
    }
  }

  return params;
}

/**
 * Converts a date to a `YYYY-MM-DD` calendar date in UTC, or `undefined` if the
 * input is missing or unparseable (`new Date("garbage").toISOString()` would
 * otherwise throw). The outcomes period is a calendar range, so only the date
 * part is sent. An omitted date param means "no date filter".
 *
 * @param {Date|string} [date]
 * @returns {string|undefined}
 */
export function toIsoDate(date) {
  if (date == null) {
    return;
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return;
  }

  return parsed.toISOString().slice(0, 10);
}

/**
 * @memberof Services
 */
class OutcomesService extends HttpClient {
  constructor(...args) {
    super(...args);
    // Array params (e.g. product_id) serialize to repeated params without
    // brackets — `product_id=1&product_id=2`
    this.http.defaults.paramsSerializer = { indexes: null };
  }

  /**
   * Dashboard summary KPIs for the viewer's scope and period.
   *
   * `metrics` narrows the response to a subset of metric keys. Required when the
   * filter envelope carries `moduleIds`: the backend rejects a module filter
   * unless every requested metric supports modules.
   *
   * @param {object} options
   * @param {object} [options.filters] - Filter envelope
   * @param {string[]} [options.metrics] - Metric keys to return; omitted returns
   *   the backend's default set.
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   */
  getDashboardSummary({ filters, metrics, signal } = {}) {
    return this.http.get("/outcomes/v1/dashboard/summary", {
      params: {
        ...encodeFilters(filters),
        ...(Array.isArray(metrics) && metrics.length > 0 ? { metrics } : {}),
      },
      signal,
    });
  }

  /**
   * The data service's metric vocabulary: every metric it serves, with its
   * label, the grains it supports, and the series it splits into. Not
   * tenant-scoped, and the only source for those attributes.
   *
   * @param {object} [options]
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   */
  getMetricCatalog({ signal } = {}) {
    return this.http.get("/outcomes/v1/metrics/catalog", { signal });
  }

  /**
   * Per-organization metric breakdown across the viewer's org subtree. Rows are
   * the child orgs of the `org_code` filter (or a flat per-org list when it is
   * omitted); each row carries the selected metric's total and its backend
   * series split. Returns one ordered page; follow `next_page_token` for the
   * next slice.
   *
   * @param {object} options
   * @param {string} options.metric - Metric to break down
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering: comma-separated `field:dir`
   *   tokens applied in order (`field`: `value`|`name`; `dir`: `asc`|`desc`),
   *   e.g. `value:desc,name:asc`. Omitted takes the backend default.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getOrgBreakdown({
    metric,
    filters,
    sort,
    includeEmpty = false,
    pageToken,
    signal,
  } = {}) {
    return fetchBreakdownPage(
      this.http,
      "/outcomes/v1/metrics/by_organization",
      {
        metric,
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Per-classroom metric breakdown within a single org. The org is a path
   * segment, so it is never sent as a filter param. Returns one ordered page;
   * follow `next_page_token` for the next slice.
   *
   * @param {object} options
   * @param {string} options.orgId - Org code to drill into (path param)
   * @param {string} options.metric - Metric to break down
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering; see
   *   {@link OutcomesService#getOrgBreakdown}.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getClassroomBreakdown({
    orgId,
    metric,
    filters,
    sort,
    includeEmpty = false,
    pageToken,
    signal,
  }) {
    return fetchBreakdownPage(
      this.http,
      `/outcomes/v1/metrics/orgs/${encodeURIComponent(orgId)}/by_classroom`,
      {
        metric,
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Per-learner metric breakdown within a single classroom. Both the org and
   * the classroom are path segments, so neither is sent as a filter param.
   * Returns one ordered page; follow `next_page_token` for the next slice.
   *
   * @param {object} options
   * @param {string} options.orgId - Org code owning the classroom (path param)
   * @param {string} options.classroomId - Classroom id to drill into (path param)
   * @param {string} options.metric - Metric to break down
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering; see
   *   {@link OutcomesService#getOrgBreakdown}.
   * @param {boolean} [options.includeAnonymous] - `false` leaves anonymous
   *   learners out before totals, sort and paging; omitted keeps everyone.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getLearnerBreakdown({
    orgId,
    classroomId,
    metric,
    filters,
    sort,
    includeAnonymous = true,
    includeEmpty = false,
    pageToken,
    signal,
  }) {
    return fetchBreakdownPage(
      this.http,
      `/outcomes/v1/metrics/orgs/${encodeURIComponent(
        orgId
      )}/classrooms/${encodeURIComponent(classroomId)}/by_learner`,
      {
        metric,
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...(includeAnonymous ? {} : { include_anonymous: false }),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Per-organization top-career breakdown: one row per org naming that org's
   * most highly-rated career. The generic by_organization endpoint's `metric`
   * enum has no slot for a ranked-career metric, so it has its own path.
   *
   * @param {object} options
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering; see {@link
   *   OutcomesService#getOrgBreakdown}. The top-career grains also order by
   *   `career`.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getTopCareersOrgBreakdown({
    filters,
    sort,
    includeEmpty = false,
    pageToken,
    signal,
  } = {}) {
    return fetchBreakdownPage(
      this.http,
      "/outcomes/v1/metrics/top_careers/by_organization",
      {
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Per-classroom top-career breakdown within a single org. The org is a path
   * segment, so it is never sent as a filter param. Classrooms on the org's
   * roster with no career activity in the period come back with a null career
   * and value 0.
   *
   * @param {object} options
   * @param {string} options.orgId - Org code to drill into (path param)
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering; see {@link
   *   OutcomesService#getTopCareersOrgBreakdown}.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getTopCareersClassroomBreakdown({
    orgId,
    filters,
    sort,
    includeEmpty = false,
    pageToken,
    signal,
  }) {
    return fetchBreakdownPage(
      this.http,
      `/outcomes/v1/metrics/orgs/${encodeURIComponent(
        orgId
      )}/top_careers/by_classroom`,
      {
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Per-learner top-career breakdown within a single classroom: the career each
   * learner rated highest. Both the org and the classroom are path segments, so
   * neither is sent as a filter param.
   *
   * These rows rank by `star_rating` (1-5), not the `value` the other two grains
   * carry: one learner rates one career once, so a highly-rated count would
   * carry no ordering.
   *
   * @param {object} options
   * @param {string} options.orgId - Org code owning the classroom (path param)
   * @param {string} options.classroomId - Classroom id to drill into (path param)
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering; see {@link
   *   OutcomesService#getTopCareersOrgBreakdown}.
   * @param {boolean} [options.includeAnonymous] - `false` leaves anonymous
   *   learners out before ranking and paging; omitted keeps everyone.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getTopCareersLearnerBreakdown({
    orgId,
    classroomId,
    filters,
    sort,
    includeAnonymous = true,
    includeEmpty = false,
    pageToken,
    signal,
  }) {
    return fetchBreakdownPage(
      this.http,
      `/outcomes/v1/metrics/orgs/${encodeURIComponent(
        orgId
      )}/classrooms/${encodeURIComponent(classroomId)}/top_careers/by_learner`,
      {
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...(includeAnonymous ? {} : { include_anonymous: false }),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Per-organization top-career-plan breakdown: one row per org naming the
   * career most often chosen in that org's submitted plans. The Trek Mobile
   * sibling of {@link OutcomesService#getTopCareersOrgBreakdown}.
   *
   * @param {object} options
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering; see {@link
   *   OutcomesService#getTopCareersOrgBreakdown}.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getTopCareerPlansOrgBreakdown({
    filters,
    sort,
    includeEmpty = false,
    pageToken,
    signal,
  } = {}) {
    return fetchBreakdownPage(
      this.http,
      "/outcomes/v1/metrics/top_career_plans/by_organization",
      {
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Per-classroom top-career-plan breakdown within a single org. The org is a
   * path segment, so it is never sent as a filter param. Classrooms with no
   * submitted plans come back with a null career and value 0.
   *
   * @param {object} options
   * @param {string} options.orgId - Org code to drill into (path param)
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering; see {@link
   *   OutcomesService#getTopCareersOrgBreakdown}.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getTopCareerPlansClassroomBreakdown({
    orgId,
    filters,
    sort,
    includeEmpty = false,
    pageToken,
    signal,
  }) {
    return fetchBreakdownPage(
      this.http,
      `/outcomes/v1/metrics/orgs/${encodeURIComponent(
        orgId
      )}/top_career_plans/by_classroom`,
      {
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Per-learner top-career-plan breakdown within a single classroom: the career
   * each learner planned most. Both the org and the classroom are path segments,
   * so neither is sent as a filter param.
   *
   * Unlike the top-careers learner grain there is no rating to rank by, so these
   * rows carry `value` like the other two grains — a plan count that is almost
   * always 1, since a learner rarely submits more than one plan per career.
   *
   * @param {object} options
   * @param {string} options.orgId - Org code owning the classroom (path param)
   * @param {string} options.classroomId - Classroom id to drill into (path param)
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering; see {@link
   *   OutcomesService#getTopCareersOrgBreakdown}.
   * @param {boolean} [options.includeAnonymous] - `false` leaves anonymous
   *   learners out before ranking and paging; omitted keeps everyone.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getTopCareerPlansLearnerBreakdown({
    orgId,
    classroomId,
    filters,
    sort,
    includeAnonymous = true,
    includeEmpty = false,
    pageToken,
    signal,
  }) {
    return fetchBreakdownPage(
      this.http,
      `/outcomes/v1/metrics/orgs/${encodeURIComponent(
        orgId
      )}/classrooms/${encodeURIComponent(
        classroomId
      )}/top_career_plans/by_learner`,
      {
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...(includeAnonymous ? {} : { include_anonymous: false }),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Per-organization top-cluster breakdown: one row per org naming the career
   * cluster its learners rated most highly. The cluster sibling of {@link
   * OutcomesService#getTopCareersOrgBreakdown}.
   *
   * @param {object} options
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering; see {@link
   *   OutcomesService#getTopCareersOrgBreakdown}.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getTopClustersOrgBreakdown({
    filters,
    sort,
    includeEmpty = false,
    pageToken,
    signal,
  } = {}) {
    return fetchBreakdownPage(
      this.http,
      "/outcomes/v1/metrics/top_clusters/by_organization",
      {
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Per-classroom top-cluster breakdown within a single org. The org is a path
   * segment, so it is never sent as a filter param.
   *
   * @param {object} options
   * @param {string} options.orgId - Org code to drill into (path param)
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering; see {@link
   *   OutcomesService#getTopCareersOrgBreakdown}.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getTopClustersClassroomBreakdown({
    orgId,
    filters,
    sort,
    includeEmpty = false,
    pageToken,
    signal,
  }) {
    return fetchBreakdownPage(
      this.http,
      `/outcomes/v1/metrics/orgs/${encodeURIComponent(
        orgId
      )}/top_clusters/by_classroom`,
      {
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Per-learner top-cluster breakdown within a single classroom. Unlike the
   * top-careers learner grain, rows keep the highly-rated count as `value`: a
   * cluster holds many careers, so one learner's count still ranks. Both the org
   * and the classroom are path segments, so neither is sent as a filter param.
   *
   * @param {object} options
   * @param {string} options.orgId - Org code owning the classroom (path param)
   * @param {string} options.classroomId - Classroom id to drill into (path param)
   * @param {object} [options.filters] - Filter envelope
   * @param {string} [options.sort] - Row ordering; see {@link
   *   OutcomesService#getTopCareersOrgBreakdown}.
   * @param {boolean} [options.includeAnonymous] - `false` leaves anonymous
   *   learners out before ranking and paging; omitted keeps everyone.
   * @param {boolean} [options.includeEmpty] - `true` keeps entities with no
   *   data for the metric; the endpoint leaves them out by default.
   * @param {string} [options.pageToken] - Cursor for the page to fetch
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  getTopClustersLearnerBreakdown({
    orgId,
    classroomId,
    filters,
    sort,
    includeAnonymous = true,
    includeEmpty = false,
    pageToken,
    signal,
  }) {
    return fetchBreakdownPage(
      this.http,
      `/outcomes/v1/metrics/orgs/${encodeURIComponent(
        orgId
      )}/classrooms/${encodeURIComponent(classroomId)}/top_clusters/by_learner`,
      {
        ...(sort ? { sort } : {}),
        ...(includeEmpty ? { include_empty: true } : {}),
        ...(includeAnonymous ? {} : { include_anonymous: false }),
        ...encodeFilters(filters),
      },
      pageToken,
      signal
    );
  }

  /**
   * Metric value over time for the viewer's scope.
   *
   * @param {object} options
   * @param {string} options.metric - Metric to plot
   * @param {"day"|"week"|"month"} [options.granularity] - Bucket size
   * @param {boolean} [options.breakdown] - Request the per-bucket `series`
   *   split (keyed by the metric's own series keys) for a multi-line chart
   * @param {object} [options.filters] - Filter envelope
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   */
  getTimeSeries({ metric, granularity, breakdown, filters, signal }) {
    return this.http.get("/outcomes/v1/metrics/timeseries", {
      params: {
        metric,
        ...(granularity ? { granularity } : {}),
        ...(breakdown ? { breakdown } : {}),
        ...encodeFilters(filters),
      },
      signal,
    });
  }

  /**
   * Careers explored in the viewer's scope, ranked by rating, with the
   * per-career rating split (`highly_rated` = 4-5 stars, `lowly_rated`,
   * `unrated`).
   *
   * Follows `next_page_token` and concatenates the pages, so `total_rows` — the
   * count behind the "+N more" affordance — is matched by rows the viewer can
   * actually reach. The returned token is the one left unfollowed: null for an
   * exhausted list, non-null only when {@link TOP_CAREERS_MAX_PAGES} stopped a
   * runaway cursor.
   *
   * @param {object} options
   * @param {object} [options.filters] - Filter envelope
   * @param {boolean} [options.includeAnonymous] - `false` leaves anonymous
   *   learners out of the ranking; omitted keeps everyone.
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  async getTopCareers({ filters, includeAnonymous, signal } = {}) {
    return this._fetchRankedList("/outcomes/v1/metrics/top_careers", {
      filters,
      includeAnonymous,
      signal,
    });
  }

  /**
   * Career plans submitted in the viewer's scope, ranked by how often each
   * career was chosen. The Trek Mobile sibling of {@link
   * OutcomesService#getTopCareers}: the rows carry a `career_id` and a single
   * `value` (a distinct plan count) in place of a simulation and a rating split.
   *
   * @param {object} options
   * @param {object} [options.filters] - Filter envelope
   * @param {boolean} [options.includeAnonymous] - `false` leaves anonymous
   *   learners out of the ranking; omitted keeps everyone.
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  async getTopCareerPlans({ filters, includeAnonymous, signal } = {}) {
    return this._fetchRankedList("/outcomes/v1/metrics/top_career_plans", {
      filters,
      includeAnonymous,
      signal,
    });
  }

  /**
   * Career clusters explored in the viewer's scope, ranked by 4-5 star
   * ratings. The same rating split as {@link OutcomesService#getTopCareers},
   * summed over each cluster's careers; `explorers` counts a learner once per
   * cluster.
   *
   * @param {object} options
   * @param {object} [options.filters] - Filter envelope
   * @param {boolean} [options.includeAnonymous] - `false` leaves anonymous
   *   learners out of the ranking; omitted keeps everyone.
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  async getTopClusters({ filters, includeAnonymous, signal } = {}) {
    return this._fetchRankedList("/outcomes/v1/metrics/top_clusters", {
      filters,
      includeAnonymous,
      signal,
    });
  }

  /**
   * Follows a ranked list's `next_page_token` and concatenates the pages, so
   * `total_rows` — the count behind the "+N more" affordance — is matched by
   * rows the viewer can actually reach. The returned token is the one left
   * unfollowed: null for an exhausted list, non-null only when {@link
   * TOP_CAREERS_MAX_PAGES} stopped a runaway cursor.
   *
   * @param {string} path
   * @param {object} options
   * @param {object} [options.filters] - Filter envelope
   * @param {boolean} [options.includeAnonymous] - `false` leaves anonymous
   *   learners out of the ranking; omitted keeps everyone.
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
   */
  async _fetchRankedList(
    path,
    { filters, includeAnonymous = true, signal } = {}
  ) {
    const params = {
      ...encodeFilters(filters),
      ...(includeAnonymous ? {} : { include_anonymous: false }),
    };
    const rows = [];
    let totalRows;
    let pageToken;

    for (let page = 0; page < TOP_CAREERS_MAX_PAGES; page += 1) {
      // The request interceptor replaces `config.signal` with its own
      // controller, so the caller's signal cannot cancel a request in flight —
      // cancellation has to land between pages.
      if (signal?.aborted) {
        throw signal.reason ?? new DOMException("Aborted", "AbortError");
      }

      const body = await this.http.get(path, {
        params: {
          ...params,
          page_size: TOP_CAREERS_PAGE_SIZE,
          ...(pageToken ? { page_token: pageToken } : {}),
        },
        signal,
      });

      if (Array.isArray(body?.data)) {
        rows.push(...body.data);
      }
      if (totalRows === undefined && Number.isInteger(body?.total_rows)) {
        totalRows = body.total_rows;
      }

      pageToken = body?.next_page_token || null;
      if (!pageToken) {
        break;
      }
    }

    return {
      data: rows,
      total_rows: totalRows ?? rows.length,
      next_page_token: pageToken || null,
    };
  }

  /**
   * Awards earned, totaled by award type, with an optional per-classroom split.
   *
   * @param {object} options
   * @param {string} [options.breakdown] - Optional secondary breakdown (e.g.
   *   "classroom")
   * @param {object} [options.filters] - Filter envelope
   * @param {AbortSignal} [options.signal] - Request cancellation signal
   */
  getAwardsBreakdown({ breakdown, filters, signal } = {}) {
    return this.http.get("/outcomes/v1/metrics/awards", {
      params: {
        ...(breakdown ? { breakdown } : {}),
        ...encodeFilters(filters),
      },
      signal,
    });
  }

  /**
   * Starts a background CSV export job for the outcomes dashboard and returns
   * its `{ job_id }`. The caller polls {@link OutcomesService#getExportStatus}
   * for completion and then {@link OutcomesService#downloadExport}s the file.
   *
   * The request body is the backend `CsvExportRequest` shape exactly (it rejects
   * unknown fields): a top-level `level`/`org_code`/`metric_ids`/`format`, and a
   * snake_case `filters` envelope. The envelope is built here rather than through
   * {@link encodeFilters} — that helper serializes query params and omits empty
   * collections, whereas the export body requires every filter key present, with
   * an empty array meaning "all in scope" and a null `product_id` meaning "all
   * products". Dates are normalized to `YYYY-MM-DD` via {@link toIsoDate}.
   *
   * @param {object} options
   * @param {string} options.level - Row granularity (e.g. `organization`,
   *   `classroom`, `learner`).
   * @param {string} options.orgCode - Parent scope org code (required).
   * @param {object} [options.filters] - Filter envelope (camelCase).
   * @param {Date|string} [options.filters.startDate] - Inclusive range start.
   * @param {Date|string} [options.filters.endDate] - Inclusive range end.
   * @param {string[]} [options.filters.orgCodes] - Org-code allowlist ([] = all).
   * @param {string[]} [options.filters.classroomIds] - Classroom UUID allowlist
   *   ([] = all).
   * @param {string[]} [options.filters.learnerIds] - Learner UUID allowlist
   *   ([] = all).
   * @param {string[]} [options.filters.programIds] - Program id allowlist
   *   ([] = all).
   * @param {string[]} [options.filters.moduleIds] - Module id allowlist
   *   ([] = all).
   * @param {number|null} [options.filters.productId] - Single product line, or
   *   null for all.
   * @param {boolean} [options.filters.includeAnonymous] - Learner grain only:
   *   `false` leaves anonymous learners out (default `true`).
   * @param {string[]} options.metricIds - Metric ids to export, in viewer order.
   * @param {string} [options.format] - Export format (default `csv`).
   * @param {string} [options.idempotencyKey] - Sent as `Idempotency-Key` so a
   *   retried submit reuses the same job rather than starting a duplicate.
   * @param {AbortSignal} [options.signal] - Request cancellation signal.
   * @returns {Promise<{ job_id: string }>}
   */
  startCsvExport({
    level,
    orgCode,
    filters = {},
    metricIds,
    format = "csv",
    idempotencyKey,
    signal,
  }) {
    const body = {
      level,
      org_code: orgCode,
      filters: {
        start_date: toIsoDate(filters.startDate) ?? null,
        end_date: toIsoDate(filters.endDate) ?? null,
        org_codes: filters.orgCodes ?? [],
        classroom_ids: filters.classroomIds ?? [],
        learner_ids: filters.learnerIds ?? [],
        program_ids: filters.programIds ?? [],
        module_ids: filters.moduleIds ?? [],
        product_id: OUTCOMES_PRODUCT_ID.get(filters.productId) ?? null,
        include_anonymous: filters.includeAnonymous ?? true,
      },
      metric_ids: metricIds,
      format,
    };

    return this.http.post("/outcome-exports/csv", body, {
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {},
      signal,
    });
  }

  /**
   * Polls a CSV export job's status. `status` is the job lifecycle state (the
   * terminal states being `ready` and `failed`); `error_message` accompanies a
   * `failed` job.
   *
   * @param {string} jobId - Export job id from {@link OutcomesService#startCsvExport}.
   * @param {object} [options]
   * @param {AbortSignal} [options.signal] - Request cancellation signal.
   * @returns {Promise<{ status: string, error_message?: string }>}
   */
  getExportStatus(jobId, { signal } = {}) {
    return this.http.get(`/outcome-exports/${encodeURIComponent(jobId)}`, {
      signal,
    });
  }

  /**
   * Downloads a finished CSV export. The endpoint returns a JSON body carrying a
   * presigned URL to the export file. The browser is navigated to that URL via a
   * synthetic anchor click: unlike `fetch`, a navigation is not subject to CORS,
   * so the cross-origin storage URL downloads directly, and storage serves the
   * saved filename via its `Content-Disposition` header.
   *
   * `fallbackName` is a best-effort `download` name for the same-origin case; a
   * cross-origin storage URL ignores it in favor of `Content-Disposition`. Its
   * org-derived component can contain characters that are unsafe in a filename,
   * so it is reduced to `[A-Za-z0-9_-]` here as defense-in-depth.
   *
   * @param {string} jobId - Export job id.
   * @param {string} [fallbackName] - Best-effort base name for the saved file.
   * @returns {Promise<boolean>} Resolves true once the download is triggered.
   */
  async downloadExport(jobId, fallbackName) {
    const safeName = String(fallbackName ?? "").replaceAll(/[^\w-]/g, "_");

    const { url } = await this.http.get(
      `/outcome-exports/${encodeURIComponent(jobId)}/download`
    );

    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${safeName}.csv`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    return true;
  }
}

export default OutcomesService;
